/**
 * titleCase — standardized title-case with PCB-domain exceptions.
 *
 * AI OCR and USPS both return ALL CAPS. Per the address pipeline order
 * (docs/port-design/people-form-port.md §2):
 *
 *   AI -> postProcessAddress -> USPS -> titleCase -> review dialog
 *
 * titleCase is applied LAST (after USPS), since USPS also returns caps.
 *
 * Exceptions preserved in original case (never title-cased):
 *   - State abbreviations (MO, IL, NY, etc.) — always uppercase
 *   - Directional 2-letter abbrs (NE, NW, SE, SW) — always uppercase
 *   - Roman-numeral suffixes (II, III, IV, V) — always uppercase
 *   - Abbreviated suffixes (JR, SR) — always uppercase
 *   - "PO" (PO Box) — always uppercase
 *   - "RR" / "HC" (rural route, highway contract route) — always uppercase;
 *     real Phelps County mailing lines: "RR 2 Box 40", "HC 65 Box 12"
 *
 * Tokens containing a digit are designators, not words, and are left exactly
 * as they arrive: "4B", "12A", "PO BOX 12B" -> "4B", "12A", "PO Box 12B".
 * Lowering "APT 4B" to "Apt 4b" reads as a different unit.
 *
 * Ordinals (1ST, 2ND, 3RD, 4TH) are NOT an exception: they title-case to
 * "1st", "2nd" like any other word. The original port (v0.1.36, 2026-04-24)
 * kept them upper with no cited reason; nothing downstream (Core Director,
 * disclosures, LaserPro) needs the USPS form, and "505 East 1st Street" is
 * how a person writes it. Agreed with the loan write-up app 2026-09-29 so
 * both apps produce one form — keep this file identical to theirs.
 */

const KEEP_UPPER = new Set([
  'PO',
  'RR', 'HC',
  'NE', 'NW', 'SE', 'SW',
  'II', 'III', 'IV', 'V',
  'JR', 'SR',
]);

/**
 * Title-case a string with PCB address/name exceptions.
 *
 * @example
 *   titleCase('123 MAIN ST APT. 1')         // '123 Main St Apt. 1'
 *   titleCase('JEFFERSON CITY')             // 'Jefferson City'
 *   titleCase('1ST AVE NW')                 // '1st Ave NW'
 *   titleCase('ROBERT SMITH JR')            // 'Robert Smith JR'
 *   titleCase('PO BOX 123')                 // 'PO Box 123'
 *   titleCase('APT 4B')                     // 'Apt 4B'
 *   titleCase('RR 2 BOX 40')                // 'RR 2 Box 40'
 *   titleCase('')                           // ''
 */
export function titleCase(s: string | undefined | null): string {
  if (!s) return '';
  return s
    .split(' ')
    .map((word) => {
      if (!word) return word;
      // Keep 2-letter state-abbr and directional markers uppercase
      if (word.length <= 3 && word === word.toUpperCase() && KEEP_UPPER.has(word)) {
        return word;
      }
      // Ordinals title-case like any other word (1ST -> 1st) — see header.
      // Must run BEFORE the digit guard below, or "1ST" would be kept as typed.
      if (/^\d+(ST|ND|RD|TH)$/i.test(word)) {
        return word.toLowerCase();
      }
      // Any other token with a digit is a designator: leave it as it arrived.
      if (/\d/.test(word)) return word;
      // Standard title case: first letter upper, rest lower
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}
